'use client'

import { useMemo } from 'react'
import type { Episode, CalendarEvent } from '@/types'
import { getMonthName } from '@/lib/date'

export interface CalendarProps {
  episodes: Episode[]
  currentMonth: string // YYYY-MM format
  today: string // YYYY-MM-DD format
  onMonthChange: (month: string) => void
  onEventClick?: (event: CalendarEvent) => void
  onDateClick?: (date: string) => void
}

interface CalendarDay {
  date: string
  day: number
  isCurrentMonth: boolean
  isToday: boolean
  events: CalendarEvent[]
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

export function Calendar({
  episodes,
  currentMonth,
  today,
  onMonthChange,
  onEventClick,
  onDateClick,
}: CalendarProps) {
  const [year, month] = currentMonth.split('-').map(Number)

  // Generate calendar events from episodes
  const events = useMemo(() => {
    const calendarEvents: CalendarEvent[] = []

    episodes.forEach((episode) => {
      // Recording event
      calendarEvents.push({
        id: `${episode.id}-recording`,
        episodeId: episode.id,
        type: 'recording',
        date: episode.recordingDate,
        title: episode.title,
        episodeNumber: episode.number,
        guestNames: episode.guestNames,
      })

      // Air date event
      calendarEvents.push({
        id: `${episode.id}-air`,
        episodeId: episode.id,
        type: 'air-date',
        date: episode.airDate,
        title: episode.title,
        episodeNumber: episode.number,
        guestNames: episode.guestNames,
      })
    })

    return calendarEvents
  }, [episodes])

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = []
    const daysInMonth = getDaysInMonth(year, month - 1)
    const firstDay = getFirstDayOfMonth(year, month - 1)

    // Previous month days
    const prevMonth = month === 1 ? 12 : month - 1
    const prevYear = month === 1 ? year - 1 : year
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth - 1)

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i
      const date = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: date === today,
        events: events.filter((e) => e.date === date),
      })
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      days.push({
        date,
        day,
        isCurrentMonth: true,
        isToday: date === today,
        events: events.filter((e) => e.date === date),
      })
    }

    // Next month days
    const remainingDays = 42 - days.length // 6 weeks
    const nextMonth = month === 12 ? 1 : month + 1
    const nextYear = month === 12 ? year + 1 : year

    for (let day = 1; day <= remainingDays; day++) {
      const date = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: date === today,
        events: events.filter((e) => e.date === date),
      })
    }

    return days
  }, [year, month, today, events])

  const handlePrevMonth = () => {
    const newMonth = month === 1 ? 12 : month - 1
    const newYear = month === 1 ? year - 1 : year
    onMonthChange(`${newYear}-${String(newMonth).padStart(2, '0')}`)
  }

  const handleNextMonth = () => {
    const newMonth = month === 12 ? 1 : month + 1
    const newYear = month === 12 ? year + 1 : year
    onMonthChange(`${newYear}-${String(newMonth).padStart(2, '0')}`)
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
      {/* Header with month/year and navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {getMonthName(month - 1)} {year}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 transition-colors"
            aria-label="Previous month"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 transition-colors"
            aria-label="Next month"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 px-6 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm text-zinc-600 dark:text-zinc-400">Recording</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm text-zinc-600 dark:text-zinc-400">Air Date</span>
        </div>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 border-b border-zinc-200 dark:border-zinc-800">
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            className="px-2 py-3 text-center text-sm font-medium text-zinc-500 dark:text-zinc-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((calendarDay, index) => (
          <div
            key={calendarDay.date}
            onClick={() => onDateClick?.(calendarDay.date)}
            className={`
              min-h-[100px] p-2 border-b border-r border-zinc-200 dark:border-zinc-800
              ${index % 7 === 6 ? 'border-r-0' : ''}
              ${index >= 35 ? 'border-b-0' : ''}
              ${calendarDay.isCurrentMonth ? 'bg-white dark:bg-zinc-900' : 'bg-zinc-50 dark:bg-zinc-900/50'}
              ${onDateClick ? 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50' : ''}
              transition-colors
            `}
          >
            {/* Day number */}
            <div
              className={`
                inline-flex items-center justify-center w-7 h-7 rounded-full text-sm mb-1
                ${calendarDay.isToday
                  ? 'bg-[#FF6F00] text-white font-semibold'
                  : calendarDay.isCurrentMonth
                    ? 'text-zinc-900 dark:text-zinc-100'
                    : 'text-zinc-400 dark:text-zinc-600'
                }
              `}
            >
              {calendarDay.day}
            </div>

            {/* Events */}
            <div className="space-y-1">
              {calendarDay.events.slice(0, 3).map((event) => (
                <button
                  key={event.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    onEventClick?.(event)
                  }}
                  className={`
                    w-full text-left px-2 py-1 rounded text-xs font-medium truncate
                    transition-opacity hover:opacity-80
                    ${event.type === 'recording'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    }
                  `}
                  title={`Ep ${event.episodeNumber}: ${event.guestNames[0] || 'TBD'}`}
                >
                  Ep {event.episodeNumber}: {event.guestNames[0] || 'TBD'}
                </button>
              ))}
              {calendarDay.events.length > 3 && (
                <div className="text-xs text-zinc-500 dark:text-zinc-400 px-2">
                  +{calendarDay.events.length - 3} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
